/* eslint-disable jsx-a11y/img-redundant-alt */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Pagination } from "@/components/ui/pagination";
import { inputClass } from "@/lib/formStyles";
import { cn } from "@/lib/utils";
import { CREATE_USER } from "api/dataProviderTypes";
import { UserRequest } from "api/types";
import DeleteDoctorDialog from "components/Others/DeleteDoctorDialog";
import ErrorWrapper from "components/Others/ErrorWrapper";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { toast } from "sonner";
import {
  createUser,
  deleteUser,
  getUsersByRole,
  updateUser,
} from "services/users";
import {
  getAdditionalRoles,
  getPrincipalRole,
  isAdmin,
  roles,
} from "../../constants/roles";
import { getLoggedInUser } from "../../utils/storageUtils";
import DashboardWrapper from "../Layout/DashboardWrapper";
import AdditionalRolesField from "../Others/AdditionalRolesField";
import RoleBadge from "../Others/RoleBadge";
import ModalUI from "../legacy/Modal";

const initialsOf = (firstName?: string, lastName?: string) =>
  ((firstName?.[0] || "") + (lastName?.[0] || "")).toUpperCase() || null;

// Variante de `inputClass` avec un padding-left adapté pour les champs
// qui portent une icône leading absolue (`absolute left-3.5 …`).
const inputWithIconClass = cn(inputClass, "pl-11");

// Form vide pour la création — on l'utilise aussi comme reset state à la
// fermeture du modal pour éviter de fuir des valeurs entre deux ouvertures
// (création après édition, par ex.).
const EMPTY_DOCTOR_FORM: any = {
  roleName: roles.ROLE_DOCTOR,
  additionalRoles: [],
  firstName: "",
  lastName: "",
  email: "",
  userName: "",
  phoneNumber: "",
  address: "",
  password: "",
  doctorQualifications: "",
};

// Variables de la mutation de suppression : le nom du remplaçant vient de
// la modale et sert uniquement au message de succès.
type DeleteDoctorVariables = {
  id: string | number;
  replacementDoctorId: number;
  replacementDoctorName: string;
};

const PersonnelMedecin = () => {
  const [form, setForm] = useState<any>(EMPTY_DOCTOR_FORM);
  // editingId === null → mode création ; sinon → édition du doctor.id.
  // Détermine quel endpoint POST/PUT envoyer et l'intitulé du modal.
  const [editingId, setEditingId] = useState<string | number | null>(null);
  // Rôle principal du compte édité : ROLE_DOCTOR à la création, mais un
  // administrateur qui exerce comme médecin apparaît aussi dans cette
  // liste — son principal reste ROLE_ADMIN et ne doit pas être proposé
  // comme rôle additionnel.
  const [editedPrincipalRole, setEditedPrincipalRole] = useState<string>(
    roles.ROLE_DOCTOR
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce de la recherche pour éviter une rafale d'appels back.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Seul un administrateur peut attribuer des rôles. On lit la session au
  // rendu — jamais au chargement du module — pour rester correct après un
  // changement de session.
  const canChangeRole = isAdmin(getLoggedInUser());

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) =>
    setForm((prev: any) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

  const {
    isLoading,
    data: resultData,
    isFetching,
    refetch,
  } = useQuery(
    ["doctors", debouncedSearch, page],
    () => getUsersByRole(roles.ROLE_DOCTOR, debouncedSearch, page, pageSize),
    {
      staleTime: 30000,
      keepPreviousData: true,
    }
  );

  const paginatedData: any = resultData?.data || {};
  const data: UserRequest[] = paginatedData.content || [];
  const errors = (resultData as any)?.errors;
  const totalPages = paginatedData.totalPages || 0;
  const totalElements = paginatedData.totalElements || 0;
  const currentPage = paginatedData.currentPage || 0;
  const hasNext = paginatedData.hasNext || false;
  const hasPrevious = paginatedData.hasPrevious || false;

  const [isOpen, setIsOpen] = useState(false);
  const [localDoctor, setLocalDoctor] = useState<UserRequest>();
  const [openFormModal, setOpenFormModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<UserRequest | null>(
    null
  );
  const localDoctorInitials = initialsOf(
    localDoctor?.firstName,
    localDoctor?.lastName
  );

  // --- Mutations -----------------------------------------------------------
  // `apiRequest` ne throw pas et `notifyApiError` toaste déjà tout `!ok`
  // côté wrapper (cf. utils/apiRequest.js). On garde seulement le contrôle
  // de flux et le toast.success — pas de `toast.error(result.message)` ni
  // de `onError` (mort).

  /**
   * Retire `additionalRoles` du payload quand l'utilisateur connecté n'est
   * pas administrateur : le back refuse (403) une attribution de rôles
   * venant d'un autre profil, ce qui ferait échouer une simple mise à jour
   * de fiche.
   */
  const withAllowedRoles = (payload: any) => {
    const { additionalRoles, ...withoutAdditionalRoles } = payload || {};
    return canChangeRole
      ? { ...withoutAdditionalRoles, additionalRoles: additionalRoles ?? [] }
      : withoutAdditionalRoles;
  };

  const { mutate: mutateCreate, isLoading: isCreatingUser } = useMutation(
    CREATE_USER,
    () => createUser(withAllowedRoles(form)),
    {
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Médecin ajouté avec succès");
        closeFormModal();
        refetch();
      },
    }
  );

  const { mutate: mutateUpdate, isLoading: isUpdatingUser } = useMutation(
    () => {
      // `password` est optionnel en édition : on ne l'envoie au back que si
      // l'utilisateur a explicitement tapé une nouvelle valeur. Sinon le
      // back risquerait de l'écraser avec une chaîne vide (ou de rejeter
      // la requête si le champ est marqué non-blank).
      // `roleName` est exclu du PUT : cet écran n'offre aucun moyen de
      // changer le rôle principal. Le back refuse (403) un `roleName`
      // envoyé par un profil non administrateur — le transmettre ferait
      // échouer une simple modification de fiche par la secrétaire. Les
      // rôles additionnels suivent la même règle, via
      // `withAllowedRoles`.
      const { password, roleName, ...rest } = form || {};
      const payload: any = { ...withAllowedRoles(rest), id: editingId };
      if (typeof password === "string" && password.length > 0) {
        payload.password = password;
      }
      return updateUser(payload);
    },
    {
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Médecin modifié avec succès");
        closeFormModal();
        refetch();
      },
    }
  );

  // Supprimer un médecin = le désactiver et transférer ses patients et ses
  // entrées de file d'attente à un autre médecin (cf. DeleteDoctorDialog) :
  // `replacementDoctorId` est exigé par le back pour ce rôle.
  const { mutate: mutateDelete, isLoading: isDeleting } = useMutation(
    (variables: DeleteDoctorVariables) =>
      deleteUser(variables.id, {
        replacementDoctorId: variables.replacementDoctorId,
      }),
    {
      onSuccess: (result: any, variables: DeleteDoctorVariables) => {
        if (!result?.ok) return;
        toast.success(
          `Médecin supprimé, patients transférés à ${variables.replacementDoctorName}.`
        );
        setDeleteCandidate(null);
        // Si on supprimait le médecin actuellement ouvert dans le drawer,
        // fermer aussi le drawer pour éviter de pointer sur un fantôme.
        if (localDoctor && (localDoctor as any).id === (deleteCandidate as any)?.id) {
          setIsOpen(false);
          setLocalDoctor(undefined);
        }
        refetch();
      },
    }
  );

  const confirmDelete = (
    replacementDoctorId: number,
    replacementDoctorName: string
  ) => {
    const doctorId = (deleteCandidate as any)?.id;
    if (doctorId == null) return;
    mutateDelete({ id: doctorId, replacementDoctorId, replacementDoctorName });
  };

  // --- Handlers ------------------------------------------------------------

  const openCreate = () => {
    setEditingId(null);
    setEditedPrincipalRole(roles.ROLE_DOCTOR);
    setForm(EMPTY_DOCTOR_FORM);
    setOpenFormModal(true);
  };

  const openEdit = (doctor: any) => {
    setEditingId(doctor?.id ?? null);
    setEditedPrincipalRole(getPrincipalRole(doctor) ?? roles.ROLE_DOCTOR);
    setForm({
      roleName: roles.ROLE_DOCTOR,
      additionalRoles: getAdditionalRoles(doctor),
      firstName: doctor?.firstName || "",
      lastName: doctor?.lastName || "",
      email: doctor?.email || "",
      userName: doctor?.userName || "",
      phoneNumber: doctor?.phoneNumber || "",
      address: doctor?.address || "",
      password: "",
      doctorQualifications: doctor?.doctorQualifications || "",
    });
    setOpenFormModal(true);
  };

  const closeFormModal = () => {
    setOpenFormModal(false);
    // Reset asynchrone pour éviter le flash visuel des champs qui se
    // vident pendant l'animation de fermeture du modal.
    setTimeout(() => {
      setEditingId(null);
      setEditedPrincipalRole(roles.ROLE_DOCTOR);
      setForm(EMPTY_DOCTOR_FORM);
    }, 200);
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    // Ne pas fermer la modale ici : c'est `onSuccess` qui le fait
    // uniquement quand result.ok est vrai. Fermer avant le retour back
    // perdrait la saisie en cas d'erreur 409 (email/username déjà
    // pris) et l'utilisateur ne verrait pas pourquoi rien ne se passe.
    if (editingId != null) {
      mutateUpdate();
    } else {
      mutateCreate();
    }
  };

  const isEditing = editingId != null;
  const isSavingForm = isCreatingUser || isUpdatingUser;

  return (
    <DashboardWrapper showSpinner={isLoading || isFetching || isSavingForm}>
      <>
        {/* Suppression d'un médecin : transfert obligatoire des patients. */}
        <DeleteDoctorDialog
          doctor={deleteCandidate}
          open={deleteCandidate != null}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteCandidate(null)}
          isLoading={isDeleting}
        />

        {/* Drawer profil docteur */}
        <ModalUI
          isOpen={isOpen}
          styles={{
            top: 0,
            left: "unset",
            transform: "unset",
            right: 0,
            height: "100%",
            width: "26rem",
            maxWidth: "92vw",
            borderRadius: 0,
            padding: 0,
          }}
          onClose={() => setIsOpen(false)}
        >
          <div className="h-full flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  Profil médecin
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-800">
                  Détails
                </h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Fermer"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
              >
                <i className="bx bx-x text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-auto px-6 py-6 space-y-6">
              {/* Carte identité */}
              <div className="flex flex-col items-center text-center">
                <div className="relative">
                  <span className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white text-2xl font-semibold ring-4 ring-white shadow-lg">
                    {localDoctorInitials || <i className="bx bx-user" />}
                  </span>
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Actif
                  </span>
                </div>
                <h3 className="mt-6 text-slate-900 text-lg font-semibold">
                  Dr {localDoctor?.firstName} {localDoctor?.lastName}
                </h3>
                {(localDoctor as any)?.userName && (
                  <p className="mt-1 text-xs text-slate-400">
                    @{(localDoctor as any).userName}
                  </p>
                )}
              </div>

              {/* Carte coordonnées */}
              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                  <i className="bx bx-id-card text-lg text-emerald-600" />
                  <h5 className="text-sm font-semibold text-slate-800">
                    Coordonnées
                  </h5>
                </header>
                <dl className="divide-y divide-slate-100">
                  <div className="px-4 py-3 flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                      <i className="bx bx-envelope text-base" />
                    </span>
                    <div className="min-w-0">
                      <dt className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                        Email
                      </dt>
                      <dd className="mt-0.5 text-sm text-slate-700 break-all">
                        {localDoctor?.email || (
                          <span className="text-slate-400 italic">
                            Non renseigné
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                  <div className="px-4 py-3 flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                      <i className="bx bx-phone text-base" />
                    </span>
                    <div className="min-w-0">
                      <dt className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                        Téléphone
                      </dt>
                      <dd className="mt-0.5 text-sm text-slate-700">
                        {localDoctor?.phoneNumber || (
                          <span className="text-slate-400 italic">
                            Non renseigné
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                  <div className="px-4 py-3 flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                      <i className="bx bx-map-pin text-base" />
                    </span>
                    <div className="min-w-0">
                      <dt className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                        Adresse
                      </dt>
                      <dd className="mt-0.5 text-sm text-slate-700">
                        {localDoctor?.address || (
                          <span className="text-slate-400 italic">
                            Non renseignée
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                </dl>
              </section>

              {/* Carte qualifications */}
              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                  <i className="bx bx-award text-lg text-emerald-600" />
                  <h5 className="text-sm font-semibold text-slate-800">
                    Qualifications
                  </h5>
                </header>
                <p className="px-4 py-3 text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                  {(localDoctor as any)?.doctorQualifications || (
                    <span className="text-slate-400 italic">
                      Aucune qualification renseignée.
                    </span>
                  )}
                </p>
              </section>
            </div>

            {/* Footer actions */}
            <div className="shrink-0 border-t border-slate-100 bg-slate-50/50 px-6 py-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setDeleteCandidate(localDoctor as any);
                }}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-rose-200 bg-white text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors px-4"
              >
                <i className="bx bx-trash text-base" />
                Supprimer
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  openEdit(localDoctor);
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-medium shadow-lg shadow-green-600/25 transition-all duration-300"
              >
                <i className="bx bx-edit-alt text-base" />
                Modifier
              </button>
            </div>
          </div>
        </ModalUI>

        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
          {/* Hero */}
          <header className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  Personnel médical
                </p>
                <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                  Médecins
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  {totalElements} médecin{totalElements > 1 ? "s" : ""} dans
                  l'équipe
                </p>
              </div>
              <button
                type="button"
                onClick={openCreate}
                className="group/btn relative inline-flex items-center gap-2 overflow-hidden self-start sm:self-end rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-medium px-5 py-3 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
                />
                <i className="bx bx-plus-medical text-lg" />
                Nouveau médecin
              </button>
            </div>
          </header>

          {/* Search bar */}
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
            <label className="relative block w-full md:w-96">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none">
                <i className="bx bx-search" />
              </span>
              <input
                type="text"
                className={cn(inputClass, "pl-11 mt-0")}
                placeholder="Rechercher un médecin…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>

          {/* Table */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    <th className="px-4 py-3 text-left">Médecin</th>
                    <th className="px-4 py-3 text-left">E-mail</th>
                    <th className="px-4 py-3 text-left">Téléphone</th>
                    <th className="px-4 py-3 text-left">Statut</th>
                    <th className="px-4 py-3 text-right" />
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-16 text-center">
                        <i className="bx bx-plus-medical text-4xl text-slate-300" />
                        <p className="mt-2 text-sm font-medium text-slate-600">
                          Aucun médecin
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          Ajustez votre recherche ou ajoutez un nouveau médecin.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    data.map((user: any, idx) => {
                      const initials = initialsOf(
                        user?.firstName,
                        user?.lastName
                      );
                      return (
                        <tr
                          key={user?.id ?? idx}
                          className="group/row border-t border-slate-100 hover:bg-emerald-50/40 transition-colors"
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-sm font-semibold text-white ring-2 ring-white shadow-sm">
                                {initials || <i className="bx bx-user" />}
                              </span>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-800 truncate">
                                  Dr {user?.firstName} {user?.lastName}
                                </p>
                                {user?.userName && (
                                  <p className="text-xs text-slate-400 truncate">
                                    @{user.userName}
                                  </p>
                                )}
                                {/* Un administrateur qui exerce comme
                                    médecin figure aussi dans cette liste :
                                    on rend ses autorités explicites. */}
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                  <RoleBadge role={getPrincipalRole(user)} />
                                  {getAdditionalRoles(user).map(
                                    (role: string) => (
                                      <RoleBadge
                                        key={role}
                                        role={role}
                                        variant="additional"
                                      />
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">
                            {user?.email || (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">
                            {user?.phoneNumber || (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              Actif
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            <div className="inline-flex items-center gap-1 opacity-70 group-hover/row:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => {
                                  setLocalDoctor(user);
                                  setIsOpen(true);
                                }}
                                className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-emerald-50 hover:text-emerald-700 transition"
                                aria-label="Voir le profil"
                                title="Voir le profil"
                              >
                                <i className="bx bx-show text-lg" />
                              </button>
                              <button
                                type="button"
                                onClick={() => openEdit(user)}
                                className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-amber-50 hover:text-amber-600 transition"
                                aria-label="Modifier"
                                title="Modifier"
                              >
                                <i className="bx bx-edit-alt text-lg" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteCandidate(user)}
                                className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
                                aria-label="Supprimer"
                                title="Supprimer"
                              >
                                <i className="bx bx-trash text-lg" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300 fill-mode-both">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalElements={totalElements}
              currentCount={data.length}
              hasNext={hasNext}
              hasPrevious={hasPrevious}
              onPageChange={setPage}
              itemLabel="médecin"
              isLoading={isLoading}
            />
          </div>

          {/* Modal — création / édition (modal partagé, mode-aware via editingId) */}
          <ModalUI
            isOpen={openFormModal}
            onClose={() => {
              if (!isSavingForm) closeFormModal();
            }}
            styles={{ height: "85%", padding: 0 }}
          >
            <form className="h-full flex flex-col">
              {/* Header */}
              <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                    {isEditing ? "Modifier le médecin" : "Nouveau médecin"}
                  </p>
                  <h2 className="mt-1 text-lg font-semibold text-slate-800 truncate">
                    {isEditing
                      ? `Dr ${form.firstName || ""} ${form.lastName || ""}`.trim() ||
                        "Modifier la fiche"
                      : "Enregistrer un médecin"}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!isSavingForm) closeFormModal();
                  }}
                  aria-label="Fermer"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
                >
                  <i className="bx bx-x text-xl" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 sm:px-8 py-6 flex-1 overflow-auto space-y-8">
                <ErrorWrapper errors={errors} />

                {/* Section Identité */}
                <section className="space-y-4">
                  <div className="flex items-center gap-2">
                    <i className="bx bx-id-card text-base text-emerald-600" />
                    <h3 className="text-sm font-semibold text-slate-700">
                      Identité
                    </h3>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700">
                        Prénom
                      </span>
                      <input
                        type="text"
                        name="firstName"
                        value={form.firstName || ""}
                        onChange={handleChange}
                        placeholder="Prénom"
                        className={inputClass}
                      />
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700">
                        Nom
                      </span>
                      <input
                        type="text"
                        name="lastName"
                        value={form.lastName || ""}
                        onChange={handleChange}
                        placeholder="Nom"
                        className={inputClass}
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="block text-sm font-medium text-slate-700">
                      Qualifications professionnelles
                    </span>
                    <textarea
                      name="doctorQualifications"
                      value={form.doctorQualifications || ""}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Spécialités, diplômes, expérience… (apparaît sur la fiche docteur)"
                      className={cn(
                        inputClass,
                        "h-auto min-h-[88px] py-2.5 resize-y leading-relaxed"
                      )}
                    />
                  </label>
                </section>

                {/* Section Rôles — attribution réservée à l'administrateur
                    (le back répond 403 pour tout autre profil). */}
                {canChangeRole && (
                  <section className="space-y-4">
                    <div className="flex items-center gap-2">
                      <i className="bx bx-shield-quarter text-base text-emerald-600" />
                      <h3 className="text-sm font-semibold text-slate-700">
                        Rôles
                      </h3>
                      <div className="flex-1 h-px bg-slate-100" />
                    </div>
                    <AdditionalRolesField
                      principalRole={editedPrincipalRole}
                      value={form?.additionalRoles ?? []}
                      onChange={(additionalRoles) =>
                        setForm((prev: any) => ({ ...prev, additionalRoles }))
                      }
                    />
                  </section>
                )}

                {/* Section Coordonnées */}
                <section className="space-y-4">
                  <div className="flex items-center gap-2">
                    <i className="bx bx-envelope text-base text-emerald-600" />
                    <h3 className="text-sm font-semibold text-slate-700">
                      Coordonnées
                    </h3>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>
                  <label className="block">
                    <span className="block text-sm font-medium text-slate-700">
                      Email
                    </span>
                    <div className="relative">
                      <i className="bx bx-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                      <input
                        type="email"
                        name="email"
                        value={form.email || ""}
                        onChange={handleChange}
                        placeholder="exemple@compassion.bj"
                        className={inputWithIconClass}
                      />
                    </div>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700">
                        Téléphone
                      </span>
                      <div className="relative">
                        <i className="bx bx-phone absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                        <input
                          type="text"
                          name="phoneNumber"
                          value={form.phoneNumber || ""}
                          onChange={handleChange}
                          placeholder="(+229) …"
                          className={inputWithIconClass}
                        />
                      </div>
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700">
                        Adresse
                      </span>
                      <div className="relative">
                        <i className="bx bx-map-pin absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                        <input
                          type="text"
                          name="address"
                          value={form.address || ""}
                          onChange={handleChange}
                          placeholder="Ville, quartier"
                          className={inputWithIconClass}
                        />
                      </div>
                    </label>
                  </div>
                </section>

                {/* Section Accès */}
                <section className="space-y-4">
                  <div className="flex items-center gap-2">
                    <i className="bx bx-lock-alt text-base text-emerald-600" />
                    <h3 className="text-sm font-semibold text-slate-700">
                      Accès au compte
                    </h3>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700">
                        Nom d'utilisateur
                      </span>
                      <div className="relative">
                        <i className="bx bx-at absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                        <input
                          type="text"
                          name="userName"
                          value={form.userName || ""}
                          onChange={handleChange}
                          placeholder="identifiant"
                          className={inputWithIconClass}
                        />
                      </div>
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700">
                        Mot de passe
                        {isEditing && (
                          <span className="ml-1 text-xs font-normal text-slate-400">
                            (laisser vide pour conserver)
                          </span>
                        )}
                      </span>
                      <div className="relative">
                        <i className="bx bx-lock-alt absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                        <input
                          type="password"
                          name="password"
                          value={form.password || ""}
                          onChange={handleChange}
                          placeholder={
                            isEditing ? "Nouveau mot de passe" : "Mot de passe initial"
                          }
                          autoComplete="new-password"
                          className={inputWithIconClass}
                        />
                      </div>
                    </label>
                  </div>
                </section>
              </div>

              {/* Footer */}
              <div className="px-6 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!isSavingForm) closeFormModal();
                  }}
                  disabled={isSavingForm}
                  className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition disabled:opacity-60"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSavingForm}
                  className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-70 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
                >
                  {isSavingForm ? (
                    <>
                      <i className="bx bx-loader-alt animate-spin text-base" />
                      <span>{isEditing ? "Enregistrement…" : "Création…"}</span>
                    </>
                  ) : (
                    <>
                      <i className={isEditing ? "bx bx-save text-lg" : "bx bx-check text-lg"} />
                      <span>{isEditing ? "Enregistrer" : "Créer"}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </ModalUI>
        </div>
      </>
    </DashboardWrapper>
  );
};

export default PersonnelMedecin;
